import { Circle, DeleteRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  Skeleton,
  Stack,
  styled,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";
import { useState } from "react";
import { AzureDevOpsIcon } from "src/assets/icons";
import { AddAzureDevOpsWorkItemModal, isWhitespace, ItemProperty, TextViewer } from "src/components";
import { AzureDevOpsWorkItemDto, useGetAzureDevOpsWorkItemCollection } from "src/http";
import { useDestructiveConfirm } from "src/utilities";

const WorkItemStack = styled(Stack)`
  .deleteButton {
    opacity: 0;
  }

  &:hover {
    .deleteButton {
      opacity: 1;
    }
  }
`;

interface Props {
  azureDevOpsWorkItemCollectionGuid: string | null;
  disabled: boolean;
  userCanEdit: boolean;
  onWorkItemAdded: (workItemId: number, includeChildren: boolean) => void;
  onWorkItemRemoved: (workItemId: number) => void;
}

const AzureDevOpsWorkItemsProperty = (props: Props) => {
  const { data, isInitialLoading } = useGetAzureDevOpsWorkItemCollection(props.azureDevOpsWorkItemCollectionGuid);
  const destructiveConfirm = useDestructiveConfirm();
  const [isAddModalVisible, setIsAddModelVisible] = useState(false);
  const theme = useTheme();

  function handleWorkItemAdded(workItemId: number, includeChildren: boolean) {
    setIsAddModelVisible(false);
    props.onWorkItemAdded(workItemId, includeChildren);
  }

  async function handleWorkItemRemoved(workItemId: number) {
    const { confirmed } = await destructiveConfirm({
      title: "Remove Link",
      content: "Are you sure you want to remove the link to this Azure DevOps work item?",
      confirmationText: "Remove",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    props.onWorkItemRemoved(workItemId);
  }

  return (
    <ItemProperty labelIcon={<AzureDevOpsIcon />} label="Azure DevOps" labelProps={{ sx: { alignSelf: "start" } }}>
      <Stack spacing={0.5}>
        {isInitialLoading && (
          <Stack spacing={0.25}>
            {[...Array(3)].map((_, i) => (
              <Stack key={i} direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                <Circle sx={{ color: theme.palette.grey[200], height: "16px", width: "16px", ml: "5px !important" }} />
                <Skeleton variant="rectangular" height="24px" width={`${300 + Math.random() * 200}px`} />
              </Stack>
            ))}
          </Stack>
        )}

        {!isInitialLoading && data && data.length > 0 && (
          <Stack spacing={0.25}>
            {data.map((workItem) => (
              <Box key={workItem.workItemId}>
                <WorkItemStack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
                  <WorkItem workItem={workItem} />
                  <IconButton
                    className="deleteButton"
                    size="small"
                    onClick={() => handleWorkItemRemoved(workItem.workItemId)}
                  >
                    <DeleteRounded />
                  </IconButton>
                </WorkItemStack>

                {workItem.children.length > 0 && (
                  <Box sx={{ pl: 1.25 }}>
                    {workItem.children.map((childWorkItem) => (
                      <WorkItem key={childWorkItem.workItemId} workItem={childWorkItem} />
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        )}

        <Button
          variant="tertiary"
          disabled={isInitialLoading}
          startIcon={<AzureDevOpsIcon fontSize="small" />}
          sx={{ alignSelf: "start", whiteSpace: "nowrap" }}
          onClick={() => setIsAddModelVisible(true)}
        >
          Add Work Item
        </Button>
      </Stack>

      <AddAzureDevOpsWorkItemModal
        visible={isAddModalVisible}
        azureDevOpsWorkItemCollectionGuid={props.azureDevOpsWorkItemCollectionGuid}
        onAddClicked={handleWorkItemAdded}
        onCancelClicked={() => setIsAddModelVisible(false)}
      />
    </ItemProperty>
  );
};

interface WorkItemProps {
  workItem: AzureDevOpsWorkItemDto;
}

const WorkItem = (props: WorkItemProps) => {
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "start" }}>
      <Tooltip title={props.workItem.state} placement="left">
        <Circle
          sx={{
            color: `#${props.workItem.stateColorHex}`,
            height: "16px",
            width: "16px",
            ml: "5px !important",
            position: "relative",
            top: 4,
          }}
        />
      </Tooltip>
      <Tooltip
        slotProps={{
          tooltip: {
            sx: {
              maxHeight: "600px",
              overflowY: "auto",
              wordBreak: "break-word",
            },
          },
        }}
        title={
          <Stack>
            <Typography variant="caption" sx={{ pl: 1.25 }}>
              {props.workItem.projectName} | {props.workItem.workItemType} {props.workItem.workItemId}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title={props.workItem.state}>
                <Circle
                  sx={{
                    color: `#${props.workItem.stateColorHex}`,
                    height: "16px",
                    width: "16px",
                    position: "relative",
                    top: 4,
                  }}
                />
              </Tooltip>
              <Stack spacing={1}>
                <Stack spacing={0.5}>
                  <Link href={props.workItem.url} target="_blank" variant="body1">
                    {props.workItem.title}
                  </Link>

                  {props.workItem.description && !isWhitespace(props.workItem.description) && (
                    <TextViewer value={props.workItem.description} />
                  )}
                </Stack>

                {props.workItem.assignedToDisplayName && (
                  <Box>
                    <Typography variant="overline">Assigned To</Typography>
                    <Typography>{props.workItem.assignedToDisplayName}</Typography>
                  </Box>
                )}

                {props.workItem.comments.length > 0 && (
                  <Box>
                    <Typography variant="overline">Most Recent Comments</Typography>
                    <Stack spacing={0.5} divider={<Divider variant="dashed" />}>
                      {props.workItem.comments.map((comment, i) => (
                        <Stack key={i} spacing={0.25}>
                          <Stack spacing={-0.25}>
                            <Typography variant="body2">{comment.authorDisplayName}</Typography>
                            <Typography variant="caption">
                              {DateTime.fromISO(comment.createdDateUtc, { zone: "utc" })
                                .toLocal()
                                .toLocaleString(DateTime.DATETIME_FULL)}
                            </Typography>
                          </Stack>
                          <TextViewer value={comment.text} />
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Stack>
          </Stack>
        }
        placement="left"
      >
        <Link href={props.workItem.url} target="_blank">
          {props.workItem.title}
        </Link>
      </Tooltip>
    </Stack>
  );
};

export { AzureDevOpsWorkItemsProperty };
